import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { useNavigate } from 'react-router-dom'
import { 
  PlusIcon, 
  DollarIcon, 
  CalendarIcon,
  SearchIcon,
  ChartIcon
} from '../components/Icons'

import { PhoneIcon } from '../components/PhoneLink'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { FullWidthContent } from '../components/Layout'
import { useViewSettings } from '../hooks/useViewSettings'
import { useBrokerContext } from '../hooks/useBrokerContext'
import { useAuth } from '../hooks/useAuth'
import ViewToggle from '../components/ViewToggle'
import DealsTableView from '../components/DealsTableView'
import DealsCompactView from '../components/DealsCompactView'
import FundingConfirmationModal from '../components/FundingConfirmationModal'
import BoardStatusPanel from '../components/BoardStatusPanel'
import FinancialValidationModal from '../components/FinancialValidationModal'
import DealValidationModal from '../components/DealValidationModal'
import UserAvatarStack from '../components/UserAvatarStack'

interface DealCustomer {
  id: number
  customer_id: number
  name: string
  email?: string
  phone?: string
  role: 'applicant' | 'co_applicant'
  order_position: number
}

interface CommissionSplit {
  id: number
  partner_id?: number
  split_type: 'percentage' | 'fixed_amount'
  split_value: number
  partner_name?: string
  partner_first_name?: string
  partner_last_name?: string
}

interface Deal {
  id: number
  name: string
  title?: string
  amount: string
  customer_name: string
  customer_phone?: string
  customer_email?: string
  customer_id: number // Keep as number for backend compatibility
  deal_type_id?: string
  type_name?: string
  lender_id?: number | null
  lender_name?: string | null
  commission?: {
    totalPoints: number
    totalPercentage: number
    grossCommission: number
    agencyFee: number
    closingFeeAmount: number
    netCommission: number
  }
  commission_splits?: CommissionSplit[]
  base_points: string
  bonus_points: string
  volume_points: string
  closing_date?: string
  expected_close_date?: string
  status_id: string
  status_name: string
  status_color: string
  status_is_archived?: boolean
  position: number
  description?: string
  created_at: string
  updated_at: string
  deal_customers?: DealCustomer[] // Applicant + co-applicants on this deal
  assignments?: DealAssignment[]
  financial_flag?: string
  board_id?: number
  net_commission?: number
}

interface Person {
  id: number
  name: string
  email?: string
  phone?: string
  is_primary: boolean
}

interface DealStatus {
  id: number
  name: string
  color: string
  position: number
  is_archived?: boolean
}

interface DealType {
  id: number
  name: string
  is_default: boolean
  position: number
}

interface Customer {
  id: number
  name: string
  email?: string
  phone?: string
}

interface DealBoard {
  id: number
  name: string
  position: number
  board_type?: 'deal' | 'lead'
  paired_board_id?: number | null
}

interface DealAssignment {
  id: number
  deal_id: number
  user_id: number
  assignment_type: 'primary' | 'collaborator'
  notes?: string
  assigned_at: string
  assigned_by: number
  first_name: string
  last_name: string
  email: string
  profile_picture_url?: string
}

interface BoardData {
  statuses: DealStatus[]
  dealsByStatus: { [statusId: string]: Deal[] }
}

// ---------------------------------------------------------------------------
// PartnerSearchField — searchable partner picker with portal dropdown
// ---------------------------------------------------------------------------
interface Partner {
  id: number
  name: string
  first_name?: string
  last_name?: string
  agency?: string
  commission_cut: number | null
}

interface PartnerSearchFieldProps {
  selected: { id: number; name: string; commission_cut: number | null } | null
  onSelect: (partner: { id: number; name: string; commission_cut: number | null }) => void
  onClear: () => void
  label: string
}

const PartnerSearchField: React.FC<PartnerSearchFieldProps> = ({ selected, onSelect, onClear, label }) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [allPartners, setAllPartners] = useState<Partner[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })

  // Fetch all partners once when the dropdown opens for the first time
  const fetchPartners = useCallback(async () => {
    if (allPartners.length > 0) return
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch('/api/partners', { headers: { Authorization: `Bearer ${token}` } })
      if (res.ok) {
        const data = await res.json()
        const list: Partner[] = (data.partners || []).map((p: any) => ({
          id: p.id,
          name: p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim(),
          first_name: p.first_name,
          last_name: p.last_name,
          agency: p.agency,
          commission_cut: p.commission_cut
        }))
        setAllPartners(list)
      }
    } catch (e) {
      console.error('Error fetching partners:', e)
    } finally {
      setLoading(false)
    }
  }, [allPartners.length])

  // Filter locally
  const results = query.trim()
    ? allPartners.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        (p.agency || '').toLowerCase().includes(query.toLowerCase())
      )
    : allPartners

  useEffect(() => {
    if (open) fetchPartners()
  }, [open, fetchPartners])

  const updateDropdownPos = useCallback(() => {
    if (!wrapperRef.current) return
    const rect = wrapperRef.current.getBoundingClientRect()
    setDropdownPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, width: rect.width })
  }, [])

  useEffect(() => {
    if (!open) return
    updateDropdownPos()
    window.addEventListener('scroll', updateDropdownPos, true)
    window.addEventListener('resize', updateDropdownPos)
    return () => {
      window.removeEventListener('scroll', updateDropdownPos, true)
      window.removeEventListener('resize', updateDropdownPos)
    }
  }, [open, updateDropdownPos])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (p: Partner) => {
    onSelect({ id: p.id, name: p.name, commission_cut: p.commission_cut })
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      {selected ? (
        <div className="flex items-center gap-2 px-3 py-2 border border-green-400 bg-green-50 dark:bg-green-900/20 dark:border-green-600 rounded-lg">
          <svg className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="flex-1 text-sm font-medium text-green-800 dark:text-green-300">{selected.name}</span>
          {selected.commission_cut != null && (
            <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-800/40 px-1.5 py-0.5 rounded">
              {selected.commission_cut}%
            </span>
          )}
          <button
            type="button"
            onClick={onClear}
            className="text-green-600 dark:text-green-400 hover:text-red-500 transition-colors ml-1"
          >
            ✕
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
          placeholder={t('partners.searchPlaceholder', { defaultValue: 'Search partners...' })}
          autoComplete="off"
        />
      )}

      {open && !selected && ReactDOM.createPortal(
        <div
          style={{ position: 'absolute', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto"
        >
          {loading ? (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
              {t('common.loading', { defaultValue: 'Loading...' })}
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
              {t('partners.noResults', { defaultValue: 'No partners found' })}
            </div>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(p) }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                    {p.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{p.name}</p>
                  {p.agency && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.agency}</p>}
                </div>
                {p.commission_cut != null && (
                  <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold shrink-0">{p.commission_cut}%</span>
                )}
              </button>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

const DealsBoard: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { activeBrokerContext } = useBrokerContext()
  const [boardData, setBoardData] = useState<BoardData>({
    statuses: [],
    dealsByStatus: {}
  })
  const [loading, setLoading] = useState(true)
  
  // View settings hook
  const { 
    settings: viewSettings, 
    loading: viewSettingsLoading, 
    toggleViewMode,
    setViewMode
  } = useViewSettings('deals_board')
  const [showAddDeal, setShowAddDeal] = useState(false)

  const [showArchivedDeals, setShowArchivedDeals] = useState(false)
  const [archivedDeals, setArchivedDeals] = useState<Deal[]>([])
  const [loadingArchived, setLoadingArchived] = useState(false)
  const [boards, setBoards] = useState<DealBoard[]>([])
  const [activeBoardId, setActiveBoardId] = useState<number | null>(null)
  // Board management UI state
  const [showBoardManager, setShowBoardManager] = useState(false)
  const [boardManagerTab, setBoardManagerTab] = useState<'list' | 'new'>('list')
  const [newBoardName, setNewBoardName] = useState('')
  const [renamingBoardId, setRenamingBoardId] = useState<number | null>(null)
  const [renamingBoardName, setRenamingBoardName] = useState('')
  const [deletingBoardId, setDeletingBoardId] = useState<number | null>(null)
  const [boardTransferTargetId, setBoardTransferTargetId] = useState<number | null>(null)
  const [boardActionLoading, setBoardActionLoading] = useState(false)
  const [showStatusPanel, setShowStatusPanel] = useState(false)
  const [showEmptyStatuses, setShowEmptyStatuses] = useState(true)
  const [dealTypes, setDealTypes] = useState<DealType[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const [selectedCustomers, setSelectedCustomers] = useState<Array<{customer_id: number, name: string, email?: string, phone?: string, role: 'applicant' | 'co_applicant'}>>([])
  const [showAssociatedCustomersPrompt, setShowAssociatedCustomersPrompt] = useState(false)
  const [associatedCustomers, setAssociatedCustomers] = useState<Customer[]>([])
  const [pendingCustomer, setPendingCustomer] = useState<Customer | null>(null)
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false)
  const [newCustomerData, setNewCustomerData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    referred_by: ''
  })
  const [creatingCustomer, setCreatingCustomer] = useState(false)

  // Referral partner selected when creating a new customer inline
  // Persists until the deal is created so we can auto-add the commission split
  interface ReferralPartner { id: number; name: string; commission_cut: number | null }
  const [selectedReferralPartner, setSelectedReferralPartner] = useState<ReferralPartner | null>(null)
  const [pendingReferralPartner, setPendingReferralPartner] = useState<ReferralPartner | null>(null)
  const [newDeal, setNewDeal] = useState({
    amount: '',
    deal_type_id: '',
    closing_date: '',
    status_id: '',
    description: ''
  })
  
  // Demo mode state for SMS integration
  const [showDemoMessage, setShowDemoMessage] = useState(false)
  const [demoMessage, setDemoMessage] = useState('')
  const [demoTimeout, setDemoTimeout] = useState<NodeJS.Timeout | null>(null)
  
  // Financial flags modal states
  const [showFundingModal, setShowFundingModal] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [pendingMove, setPendingMove] = useState<{
    deal: Deal
    destinationStatusId: number
    destinationIndex: number
  } | null>(null)
  const [validationError, setValidationError] = useState<{
    type: 'skip_funding' | 'invalid_transition'
    currentFlag: string
    attemptedFlag: string
  } | null>(null)

  // Deal status rules validation modal (missing required fields)
  const [showDealValidationModal, setShowDealValidationModal] = useState(false)
  const [dealValidationState, setDealValidationState] = useState<{
    dealId: number
    dealData: Record<string, any>
    missingFields: string[]
    targetStatusId: number
    targetStatusName: string
  } | null>(null)

  useEffect(() => {
    fetchBoards()
    fetchDealTypes()
  }, [])

  // Re-fetch deals whenever the active board changes (including initial load once boards arrive)
  useEffect(() => {
    if (activeBoardId !== null) {
      fetchDealsData()
    }
  }, [activeBoardId])

  // Track in-flight drag-and-drop moves so focus events don't race against them
  const movingRef = React.useRef(false)

  // Re-fetch when the window regains focus (e.g. returning from DealDetail)
  // Skip if a move is in progress — the move itself will refresh after it completes
  useEffect(() => {
    const onFocus = () => {
      if (activeBoardId !== null && !movingRef.current) fetchDealsData()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [activeBoardId])

  useEffect(() => {
    // Debounce customer search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      if (customerSearch.trim()) {
        searchCustomers(customerSearch)
      } else {
        setCustomers([])
        setShowCustomerDropdown(false)
      }
    }, 300) // 300ms delay

    return () => clearTimeout(timeoutId)
  }, [customerSearch])

  // Cleanup demo timeout on unmount
  useEffect(() => {
    return () => {
      if (demoTimeout) {
        clearTimeout(demoTimeout)
      }
    }
  }, [demoTimeout])

  const fetchDealsData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const params = new URLSearchParams({ _t: String(Date.now()) })
      if (activeBoardId) params.set('board_id', String(activeBoardId))
      const response = await makeApiCall(`api/deals?${params.toString()}`)

      if (response.ok) {
        const data = await response.json()
        const deals: Deal[] = data.deals || []
        const statuses: DealStatus[] = [...(data.statuses || [])]

        const dealsWithCustomers = deals.map((deal) => ({
          ...deal,
          deal_customers: deal.deal_customers || []
        }))

        // Group every deal into its status column. Never drop a deal just
        // because its status wasn't in the board's column list.
        const dealsByStatus: { [statusId: string]: Deal[] } = {}
        statuses.forEach((status) => {
          dealsByStatus[String(status.id)] = []
        })

        dealsWithCustomers.forEach((deal) => {
          const key = String(deal.status_id)
          if (!dealsByStatus[key]) {
            dealsByStatus[key] = []
            if (!statuses.some((s) => String(s.id) === key)) {
              statuses.push({
                id: Number(deal.status_id),
                name: deal.status_name || 'Unknown',
                color: deal.status_color || '#6B7280',
                position: statuses.length,
                is_archived: deal.status_is_archived
              })
            }
          }
          dealsByStatus[key].push(deal)
        })

        setBoardData({
          statuses,
          dealsByStatus
        })
      }
    } catch (error) {
      console.error('Error fetching deals:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchArchivedDeals = async () => {
    setLoadingArchived(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await makeApiCall('api/deals/archived')

      if (response.ok) {
        const data = await response.json()
        setArchivedDeals(data.deals || [])
      }
    } catch (error) {
      console.error('Error fetching archived deals:', error)
    } finally {
      setLoadingArchived(false)
    }
  }

  const fetchBoards = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await makeApiCall('api/boards')

      if (response.ok) {
        const data: DealBoard[] = await response.json()
        setBoards(data)
        // Default to the first deal board (position 0 = Main Board)
        if (activeBoardId === null) {
          const firstDeal = data.find(b => b.board_type === 'deal' || !b.board_type) ?? data[0]
          if (firstDeal) setActiveBoardId(firstDeal.id)
        }
      }
    } catch (error) {
      console.error('Error fetching boards:', error)
    }
  }

  const createBoard = async () => {
    if (!newBoardName.trim()) return
    setBoardActionLoading(true)
    try {
      const res = await makeApiCall('api/boards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newBoardName.trim() })
      })
      if (res.ok) {
        setNewBoardName('')
        setBoardManagerTab('list')
        await fetchBoards()
      }
    } finally {
      setBoardActionLoading(false)
    }
  }

  const renameBoard = async (boardId: number) => {
    if (!renamingBoardName.trim()) return
    setBoardActionLoading(true)
    try {
      const res = await makeApiCall(`api/boards/${boardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: renamingBoardName.trim() })
      })
      if (res.ok) {
        setRenamingBoardId(null)
        await fetchBoards()
      }
    } finally {
      setBoardActionLoading(false)
    }
  }

  const deleteBoard = async (boardId: number) => {
    setBoardActionLoading(true)
    try {
      const body: any = {}
      if (boardTransferTargetId) body.target_board_id = boardTransferTargetId
      const res = await makeApiCall(`api/boards/${boardId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (res.ok) {
        setDeletingBoardId(null)
        setBoardTransferTargetId(null)
        if (activeBoardId === boardId) setActiveBoardId(null)
        await fetchBoards()
      } else {
        const err = await res.json()
        alert(err.message || 'Cannot delete board')
      }
    } finally {
      setBoardActionLoading(false)
    }
  }

  const sendToDealBoard = async (dealId: number) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await makeApiCall(`api/deals/${dealId}/send-to-deal-board`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        fetchDealsData()
        fetchBoards()
      } else {
        const err = await response.json()
        console.error('send-to-deal-board error', err)
      }
    } catch (error) {
      console.error('Error sending deal to deal board:', error)
    }
  }

  const fetchDealTypes = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await makeApiCall('api/settings/deal-types')

      if (response.ok) {
        const data = await response.json()
        const dealTypesArray = data.deal_types || []
        setDealTypes(dealTypesArray)
        
        // Set default deal type (first one)
        if (dealTypesArray.length > 0) {
          setNewDeal(prev => ({ ...prev, deal_type_id: dealTypesArray[0].id.toString() }))
        }
      }
    } catch (error) {
      console.error('Error fetching deal types:', error)
    }
  }

  const searchCustomers = async (searchTerm: string) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await makeApiCall(`api/deals/customers/search?q=${encodeURIComponent(searchTerm)}&limit=10`)

      if (response.ok) {
        const data = await response.json()
        setCustomers(data.customers)
        setShowCustomerDropdown(true)
      }
    } catch (error) {
      console.error('Error searching customers:', error)
    }
  }

  const fetchAssociatedCustomers = async (customerId: number): Promise<Customer[]> => {
    try {
      const response = await makeApiCall(`api/customer-associations/${customerId}`)
      if (response.ok) {
        const data = await response.json()
        return data.associations || []
      }
    } catch (error) {
      console.error('Error fetching associated customers:', error)
    }
    return []
  }

  const addCustomerToSelection = (customer: Customer, role?: 'applicant' | 'co_applicant') => {
    // Check if customer is already selected
    if (selectedCustomers.some(sc => sc.customer_id === customer.id)) {
      return false
    }

    // First customer is applicant, others are co-applicants
    const customerRole = role || (selectedCustomers.length === 0 ? 'applicant' : 'co_applicant')
    
    setSelectedCustomers(prev => [...prev, {
      customer_id: customer.id,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      role: customerRole
    }])
    
    return true
  }

  const handleAddCustomer = async (customer: Customer) => {
    // Check if customer is already selected
    if (selectedCustomers.some(sc => sc.customer_id === customer.id)) {
      alert('This customer is already added to the deal')
      return
    }

    // Fetch associated customers
    const associated = await fetchAssociatedCustomers(customer.id)
    
    // Filter out any associated customers that are already selected
    const availableAssociated = associated.filter(ac => 
      !selectedCustomers.some(sc => sc.customer_id === ac.id) && ac.id !== customer.id
    )

    // Add the selected customer first
    addCustomerToSelection(customer)
    
    setCustomerSearch('')
    setShowCustomerDropdown(false)

    // If there are associated customers, show prompt
    if (availableAssociated.length > 0) {
      setAssociatedCustomers(availableAssociated)
      setPendingCustomer(customer)
      setShowAssociatedCustomersPrompt(true)
    }
  }

  const handleAddAllAssociatedCustomers = () => {
    // Add all associated customers as co-applicants
    associatedCustomers.forEach(customer => {
      addCustomerToSelection(customer, 'co_applicant')
    })
    setShowAssociatedCustomersPrompt(false)
    setAssociatedCustomers([])
    setPendingCustomer(null)
  }

  const handleSkipAssociatedCustomers = () => {
    setShowAssociatedCustomersPrompt(false)
    setAssociatedCustomers([])
    setPendingCustomer(null)
  }

  const handleCreateCustomer = async () => {
    // Validation
    if (!newCustomerData.first_name || !newCustomerData.last_name || !newCustomerData.email) {
      alert(t('deals.errors.customerFieldsRequired', { defaultValue: 'Please fill in first name, last name, and email.' }))
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newCustomerData.email)) {
      alert(t('deals.errors.invalidEmail', { defaultValue: 'Please enter a valid email address.' }))
      return
    }

    setCreatingCustomer(true)
    try {
      const payload: Record<string, any> = { ...newCustomerData }
      if (selectedReferralPartner) payload.partner_id = selectedReferralPartner.id

      const response = await makeJsonApiCall('/api/customers', 'POST', payload)

      if (response.ok) {
        const { customer: createdCustomer } = await response.json()
        
        // Add the newly created customer to the deal
        const customerForDeal: Customer = {
          id: createdCustomer.id,
          name: `${createdCustomer.first_name} ${createdCustomer.last_name}`,
          email: createdCustomer.email,
          phone: createdCustomer.phone
        }
        
        addCustomerToSelection(customerForDeal)

        // Remember partner for commission split after deal is created
        if (selectedReferralPartner) {
          setPendingReferralPartner(selectedReferralPartner)
        }
        
        // Close modal and reset form
        setShowCreateCustomerModal(false)
        setNewCustomerData({ first_name: '', last_name: '', email: '', phone: '', referred_by: '' })
        setSelectedReferralPartner(null)
        setCustomerSearch('')
      } else {
        const errorData = await response.json()
        alert(t('deals.errors.customerCreateFailed', { defaultValue: errorData.error || 'Failed to create customer. Please try again.' }))
      }
    } catch (error) {
      console.error('Error creating customer:', error)
      alert(t('deals.errors.networkError', { defaultValue: 'Network error. Please check your connection and try again.' }))
    } finally {
      setCreatingCustomer(false)
    }
  }

  const handleRemoveCustomer = (customerId: number) => {
    setSelectedCustomers(prev => {
      const updated = prev.filter(sc => sc.customer_id !== customerId)
      // Ensure there's always one applicant if customers remain
      if (updated.length > 0 && !updated.some(sc => sc.role === 'applicant')) {
        updated[0].role = 'applicant'
      }
      return updated
    })
  }

  const handleChangeCustomerRole = (customerId: number, newRole: 'applicant' | 'co_applicant') => {
    setSelectedCustomers(prev => prev.map(sc => {
      if (sc.customer_id === customerId) {
        return { ...sc, role: newRole }
      }
      // If making this customer applicant, change current applicant to co-applicant
      if (newRole === 'applicant' && sc.role === 'applicant') {
        return { ...sc, role: 'co_applicant' }
      }
      return sc
    }))
  }

  const findDealById = (id: string): Deal | undefined => {
    for (const deals of Object.values(boardData.dealsByStatus)) {
      const deal = deals.find(d => d.id.toString() === id)
      if (deal) return deal
    }
    return undefined
  }

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result

    if (!destination) return
    if (destination.droppableId === source.droppableId && destination.index === source.index) return

    const deal = findDealById(draggableId)
    if (!deal) return

    // Check if destination status is archived
    const destinationStatus = boardData.statuses.find(s => s.id.toString() === destination.droppableId)
    const isMovingToArchived = destinationStatus?.is_archived

    // If moving to archived status, remove from board but don't add to destination
    if (isMovingToArchived) {
      // Optimistically remove from source
      const newDealsByStatus = { ...boardData.dealsByStatus }
      const sourceDeals = Array.from(newDealsByStatus[source.droppableId])
      sourceDeals.splice(source.index, 1)
      newDealsByStatus[source.droppableId] = sourceDeals
      setBoardData(prev => ({ ...prev, dealsByStatus: newDealsByStatus }))
    } else {
      // Normal move between statuses
      // Optimistically update UI
      const newDealsByStatus = { ...boardData.dealsByStatus }
      
      // Remove from source
      const sourceDeals = Array.from(newDealsByStatus[source.droppableId])
      sourceDeals.splice(source.index, 1)
      newDealsByStatus[source.droppableId] = sourceDeals

      // Add to destination
      const destDeals = Array.from(newDealsByStatus[destination.droppableId])
      destDeals.splice(destination.index, 0, deal)
      newDealsByStatus[destination.droppableId] = destDeals

      setBoardData(prev => ({ ...prev, dealsByStatus: newDealsByStatus }))
    }

    // Update backend
    movingRef.current = true
    try {
      const response = await makeJsonApiCall(`/api/deals/${draggableId}/move`, 'PUT', {
        status_id: parseInt(destination.droppableId),
        position: destination.index
      })
      
      const responseData = await response.json()
      
      if (!response.ok) {
        if (responseData.requiresValidation && responseData.missingFields?.length) {
          setDealValidationState({
            dealId: parseInt(draggableId),
            dealData: deal as unknown as Record<string, any>,
            missingFields: responseData.missingFields,
            targetStatusId: parseInt(destination.droppableId),
            targetStatusName: destinationStatus?.name || 'next status'
          })
          setShowDealValidationModal(true)
        } else if (responseData.validationError) {
          setValidationError({
            type: responseData.validationError,
            currentFlag: responseData.currentFlag || 'unknown',
            attemptedFlag: responseData.requiredFlag || 'unknown'
          })
          setShowValidationModal(true)
        }
        await fetchDealsData()
        return
      }
      
      if (responseData.requiresFundingConfirmation) {
        setPendingMove({
          deal,
          destinationStatusId: parseInt(destination.droppableId),
          destinationIndex: destination.index
        })
        setShowFundingModal(true)
        await fetchDealsData()
        return
      }
      
      // Re-fetch after successful move so board reflects server state.
      // Awaiting here keeps movingRef.current = true until fresh data lands,
      // preventing a concurrent window-focus fetch from overwriting the update.
      await fetchDealsData()

      if (destination.droppableId !== source.droppableId) {
        showSmsDemo(deal)
      }
    } catch (error: any) {
      console.error('Error moving deal:', error)
      await fetchDealsData()
    } finally {
      movingRef.current = false
    }
  }



  const handleFundingConfirm = async (fundingData: {
    amount: number
    base_points: number
    bonus_points: number
    volume_points: number
  }) => {
    if (!pendingMove) return

    try {
      const token = localStorage.getItem('token')
      await makeJsonApiCall(`/api/deals/${pendingMove.deal.id}/move`, 'PUT', {
        status_id: pendingMove.destinationStatusId,
        position: pendingMove.destinationIndex,
        fundingConfirmed: true,
        fundingData
      })
      
      // Close modal and clear pending move
      setShowFundingModal(false)
      setPendingMove(null)
      
      // Refresh data to get updated deal
      await fetchDealsData()
      
      // Show demo SMS
      showSmsDemo(pendingMove.deal)
    } catch (error) {
      console.error('Error confirming funding:', error)
      alert('Failed to confirm funding. Please try again.')
    }
  }

  const handleFundingCancel = () => {
    setShowFundingModal(false)
    setPendingMove(null)
  }

  const handleValidationClose = () => {
    setShowValidationModal(false)
    setValidationError(null)
  }

  const handleDealClick = (deal: Deal) => {
    navigate(`/dashboard/deals/${deal.id}`)
  }

  const handleScenarioClick = (e: React.MouseEvent, deal: Deal) => {
    e.stopPropagation() // Prevent triggering the deal click
    navigate(`/dashboard/deals/${deal.id}/scenarios`)
  }

  const handleDealSave = (updatedDeal: Deal) => {
    setBoardData(prev => {
      const newDealsByStatus = { ...prev.dealsByStatus }
      Object.keys(newDealsByStatus).forEach(statusId => {
        const dealIndex = newDealsByStatus[statusId].findIndex(d => d.id === updatedDeal.id)
        if (dealIndex !== -1) {
          // Preserve the existing deal's properties that might be missing in the updated deal
          const existingDeal = newDealsByStatus[statusId][dealIndex]
          newDealsByStatus[statusId][dealIndex] = {
            ...existingDeal,
            ...updatedDeal,
            status_id: statusId
          }
        }
      })
      return { ...prev, dealsByStatus: newDealsByStatus }
    })
  }

  // Add handleDealDelete function
  const handleDealDelete = (deletedDeal: Deal) => {
    setBoardData(prev => {
      const newDealsByStatus = { ...prev.dealsByStatus }
      Object.keys(newDealsByStatus).forEach(statusId => {
        newDealsByStatus[statusId] = newDealsByStatus[statusId].filter(d => d.id !== deletedDeal.id)
      })
      return { ...prev, dealsByStatus: newDealsByStatus }
    })
  }

  // Show demo message for SMS (when Twilio is not configured)
  const showSmsDemo = async (deal: Deal) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      // Check if we have a demo message for this status
      const response = await makeApiCall(`/api/deals/${deal.id}/demo-sms`)

      if (response.ok) {
        const data = await response.json()
        // Only show demo banner if there's actually a demo message
        // If demoMessage is null, Twilio is configured and live
        if (data.demoMessage && data.demoMessage !== 'No demo message available') {
          setDemoMessage(data.demoMessage)
          setShowDemoMessage(true)
          
          // Clear previous timeout
          if (demoTimeout) {
            clearTimeout(demoTimeout)
          }
          
          // Auto-hide after 5 seconds
          const timeout = setTimeout(() => {
            setShowDemoMessage(false)
          }, 5000)
          setDemoTimeout(timeout)
        }
      }
    } catch (error) {
      console.error('Error fetching demo SMS message:', error)
    }
  }

  const handleAddDeal = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!newDeal.status_id) {
      alert(t('deals.errors.statusRequired', { defaultValue: 'Please select an initial status for the deal.' }))
      return
    }
    
    if (selectedCustomers.length === 0) {
      alert(t('deals.errors.customerRequired', { defaultValue: 'Please add at least one customer to the deal.' }))
      return
    }

    if (!newDeal.amount || parseFloat(newDeal.amount.replace(/[^0-9.-]/g, '')) <= 0) {
      alert(t('deals.errors.amountRequired', { defaultValue: 'Please enter a valid loan amount.' }))
      return
    }

    try {
      const token = localStorage.getItem('token')
      
      // Generate deal title from first customer name and deal type
      const applicant = selectedCustomers.find(c => c.role === 'applicant') || selectedCustomers[0]
      const dealTypeName = dealTypes.find(dt => dt.id.toString() === newDeal.deal_type_id)?.name || 'Deal'
      const generatedTitle = `${applicant.name} - ${dealTypeName}`
      
      const response = await makeJsonApiCall('/api/deals', 'POST', {
        title: generatedTitle,
        amount: newDeal.amount,
        customers: selectedCustomers.map((customer, index) => ({
          customer_id: customer.customer_id,
          role: customer.role
        })),
        deal_type_id: newDeal.deal_type_id ? parseInt(newDeal.deal_type_id) : undefined,
        base_points: 0,
        bonus_points: 0,
        volume_points: 0,
        closing_date: newDeal.closing_date || undefined,
        status_id: parseInt(newDeal.status_id),
        description: newDeal.description
      })

      if (response.ok) {
        const createdDeal = await response.json()

        // Auto-add commission split for referral partner (mirrors lead conversion behaviour)
        if (pendingReferralPartner) {
          try {
            await makeJsonApiCall('/api/commission-splits', 'POST', {
              deal_id: createdDeal.id,
              partner_id: pendingReferralPartner.id,
              split_type: 'percentage',
              split_value: pendingReferralPartner.commission_cut ?? 0,
              notes: 'Auto-added from referral partner'
            })
          } catch (e) {
            console.error('Failed to add referral commission split:', e)
          }
          setPendingReferralPartner(null)
        }

        setShowAddDeal(false)
        setNewDeal({
          amount: '',
          deal_type_id: dealTypes.find(t => t.is_default)?.id.toString() || '',
          closing_date: '',
          status_id: '',
          description: ''
        })
        setSelectedCustomers([])
        setCustomerSearch('')
        fetchDealsData()
        
        // Redirect to the deal detail page
        navigate(`/dashboard/deals/${createdDeal.id}`)
      } else {
        const errorData = await response.json()
        console.error('Error creating deal:', errorData)
        alert(t('deals.errors.createFailed', { defaultValue: errorData.error || 'Failed to create deal. Please check your input and try again.' }))
      }
    } catch (error) {
      console.error('Error creating deal:', error)
      alert(t('deals.errors.networkError', { defaultValue: 'Network error. Please check your connection and try again.' }))
    }
  }

  const formatCompactCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(num) || num === 0) return '$0'
    
    if (num >= 1000000) {
      // Format as millions (e.g., 1M or 1.2M for significant decimals only)
      const millions = num / 1000000
      return millions % 1 === 0 ? `$${Math.round(millions)}M` : `$${millions.toFixed(1)}M`
    } else if (num >= 1000) {
      // Format as thousands (e.g., 854K, no decimals)
      return `$${Math.round(num / 1000)}K`
    } else {
      // Less than 1000, show full amount without decimals
      return `$${Math.round(num)}`
    }
  }

  const formatCurrency = (amount: string | number) => {
    // Handle edge cases where amount might be an object or invalid value
    if (amount === null || amount === undefined || amount === '') {
      return '$0'
    }
    
    // If it's an object, try to extract a numeric value or default to 0
    if (typeof amount === 'object') {
      return '$0'
    }
    
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    
    // Check if the parsed value is a valid number
    if (isNaN(numericAmount)) {
      return '$0'
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  const formatAmountInMillions = (amount: number) => {
    if (amount === 0) return ''
    const millions = amount / 1000000
    if (millions >= 1) {
      return `${millions.toFixed(1)}M`
    } else if (amount >= 100000) {
      return `${millions.toFixed(2)}M`
    } else {
      return formatCurrency(amount)
    }
  }

  // Memoize net commission totals per status column
  const statusTotals = useMemo(() => {
    const totals: { [statusId: number]: number } = {}
    boardData.statuses.forEach(status => {
      const deals = boardData.dealsByStatus[String(status.id)] || []
      totals[status.id] = deals.reduce((sum, deal) => {
        // Prefer pre-computed net_commission from cache; fall back to commission object
        const nc =
          deal.net_commission ??
          deal.commission?.netCommission ??
          0
        return sum + (typeof nc === 'string' ? parseFloat(nc) || 0 : nc)
      }, 0)
    })
    return totals
  }, [boardData])

  const getDealTypeColor = (title: string) => {
    switch (title?.toLowerCase()) {
      case 'purchase':
        return 'from-gray-800 to-cyan-600'
      case 'refinance':
        return 'from-green-500 to-emerald-600'
      case 'transfer':
        return 'from-gray-800 to-pink-600'
      default:
        return 'from-gray-500 to-gray-600'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <FullWidthContent>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('deals.title')}
            </h1>
            
            {/* Broker Context Indicator for Multi-Broker Collaborators */}
            {user?.role === 'collaborator' && activeBrokerContext && (
              <div className="mt-3 inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-2">
                  <span className="text-gray-900 dark:text-gray-600 font-medium text-xs">
                    {activeBrokerContext.brokerName.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">
                  Viewing deals from: {activeBrokerContext.brokerName}
                </span>
              </div>
            )}
          </div>
        <div className="flex items-center space-x-2">
          {/* View switcher — icon buttons */}
          {viewSettings && (
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 space-x-0.5">
              <button
                onClick={() => setViewMode('cards')}
                title="Card view"
                className={`p-1.5 rounded-md transition-colors ${
                  viewSettings.viewMode === 'cards'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('table')}
                title="Table view"
                className={`p-1.5 rounded-md transition-colors ${
                  viewSettings.viewMode === 'table'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('compact')}
                title="Compact view"
                className={`p-1.5 rounded-md transition-colors ${
                  viewSettings.viewMode === 'compact'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </button>
            </div>
          )}

          {/* Archive button */}
          <button
            onClick={() => { setShowArchivedDeals(true); fetchArchivedDeals() }}
            title={t('deals.viewArchived')}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>

          {/* Board status editor */}
          <button
            onClick={() => setShowStatusPanel(true)}
            title="Edit board statuses"
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          <button
            onClick={() => setShowAddDeal(true)}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
          >
            <PlusIcon size={20} className="mr-2" />
            {t('deals.addDeal')}
          </button>
        </div>
      </div>

      {/* Board selector tabs with management */}
      {boards.length > 0 && (() => {
        const activeBoard = boards.find(b => b.id === activeBoardId)
        const pairedBoard = activeBoard?.paired_board_id
          ? boards.find(b => b.id === activeBoard.paired_board_id)
          : null
        const dealBoards = boards.filter(b => b.board_type !== 'lead')
        const isOnLeadBoard = activeBoard?.board_type === 'lead'

        return (
          <>
            {/* ── Paired board navigation bar ─────────────────────────────── */}
            {pairedBoard && (
              <div className="flex items-stretch rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm mb-3">
                {/* Left wing — Lead Board */}
                <button
                  onClick={() => {
                    const leadBoard = boards.find(b => b.board_type === 'lead' && (b.id === pairedBoard.id || b.paired_board_id === activeBoard?.id || activeBoard?.paired_board_id === b.id))
                    if (leadBoard) setActiveBoardId(leadBoard.id)
                    else if (pairedBoard.board_type === 'lead') setActiveBoardId(pairedBoard.id)
                  }}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors flex-1 border-r border-gray-200 dark:border-gray-700 ${
                    isOnLeadBoard
                      ? 'bg-indigo-600 text-white'
                      : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                  }`}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs font-normal opacity-70">Switch to</div>
                    <div>{isOnLeadBoard ? (activeBoard?.name ?? 'Lead Board') : (pairedBoard.board_type === 'lead' ? pairedBoard.name : 'Lead Board')}</div>
                  </div>
                  {isOnLeadBoard && (
                    <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">Active</span>
                  )}
                </button>

                {/* Centre — current board name */}
                <div className="flex items-center justify-center px-6 py-3 flex-shrink-0 text-sm font-bold text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/40">
                  {isOnLeadBoard ? '← Lead Board' : activeBoard?.name ?? 'Deal Board'}
                </div>

                {/* Right wing — Deal Board */}
                <button
                  onClick={() => {
                    const dealBoard = boards.find(b => b.board_type !== 'lead' && (b.id === pairedBoard.id || b.paired_board_id === activeBoard?.id || activeBoard?.paired_board_id === b.id))
                    if (dealBoard) setActiveBoardId(dealBoard.id)
                    else if (pairedBoard.board_type !== 'lead') setActiveBoardId(pairedBoard.id)
                  }}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors flex-1 justify-end border-l border-gray-200 dark:border-gray-700 ${
                    !isOnLeadBoard
                      ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {!isOnLeadBoard && (
                    <span className="mr-auto text-xs bg-white/20 dark:bg-black/10 px-2 py-0.5 rounded-full">Active</span>
                  )}
                  <div className="text-right">
                    <div className="text-xs font-normal opacity-70">Switch to</div>
                    <div>{!isOnLeadBoard ? (activeBoard?.name ?? 'Deal Board') : (pairedBoard.board_type !== 'lead' ? pairedBoard.name : 'Deal Board')}</div>
                  </div>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            {/* ── Board tabs + management ──────────────────────────────────── */}
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
              {/* Board tabs (deal boards only) */}
              <div className="flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto">
                {dealBoards.map((board) => (
                  <button
                    key={board.id}
                    onClick={() => setActiveBoardId(board.id)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors flex-shrink-0 ${
                      activeBoardId === board.id && !isOnLeadBoard
                        ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {board.name}
                  </button>
                ))}
                <button
                  onClick={() => { setShowBoardManager(true); setBoardManagerTab('new') }}
                  title="Add board"
                  className="px-2 py-2 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 border-b-2 border-transparent flex-shrink-0 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Manage button */}
              <button
                onClick={() => { setShowBoardManager(true); setBoardManagerTab('list') }}
                title="Manage boards"
                className="flex items-center gap-1.5 px-3 py-1.5 ml-3 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Manage
              </button>
            </div>

            {/* Board Manager Modal */}
            {showBoardManager && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowBoardManager(false)}>
                <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
                  {/* Header */}
                  <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Manage Boards</h3>
                    <button onClick={() => setShowBoardManager(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>

                  {/* Tabs */}
                  <div className="flex border-b border-gray-200 dark:border-gray-700">
                    {(['list', 'new'] as const).map(tab => (
                      <button key={tab} onClick={() => setBoardManagerTab(tab)}
                        className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${boardManagerTab === tab ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                        {tab === 'list' ? 'My Boards' : 'New Board'}
                      </button>
                    ))}
                  </div>

                  <div className="p-5">
                    {boardManagerTab === 'list' ? (
                      <ul className="space-y-2">
                        {dealBoards.map(board => (
                          <li key={board.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                            {renamingBoardId === board.id ? (
                              <input
                                autoFocus
                                value={renamingBoardName}
                                onChange={e => setRenamingBoardName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') renameBoard(board.id); if (e.key === 'Escape') setRenamingBoardId(null) }}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              />
                            ) : (
                              <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white truncate">{board.name}</span>
                            )}

                            {renamingBoardId === board.id ? (
                              <div className="flex gap-1">
                                <button onClick={() => renameBoard(board.id)} disabled={boardActionLoading} className="px-2 py-1 text-xs bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50">Save</button>
                                <button onClick={() => setRenamingBoardId(null)} className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800">Cancel</button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <button onClick={() => { setRenamingBoardId(board.id); setRenamingBoardName(board.name) }}
                                  className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" title="Rename">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                </button>
                                {board.position !== 0 && (
                                  <button onClick={() => { setDeletingBoardId(board.id); setBoardTransferTargetId(null) }}
                                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Delete">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                )}
                              </div>
                            )}
                          </li>
                        ))}

                        {/* Delete confirmation inline */}
                        {deletingBoardId && (() => {
                          const boardToDelete = boards.find(b => b.id === deletingBoardId)
                          const otherBoards = dealBoards.filter(b => b.id !== deletingBoardId)
                          return (
                            <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                              <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">Delete "{boardToDelete?.name}"?</p>
                              {otherBoards.length > 0 && (
                                <div className="mb-3">
                                  <label className="text-xs text-red-700 dark:text-red-400 mb-1 block">Transfer deals to:</label>
                                  <select value={boardTransferTargetId ?? ''} onChange={e => setBoardTransferTargetId(Number(e.target.value) || null)}
                                    className="w-full px-2 py-1.5 text-sm border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                                    <option value="">— select a board —</option>
                                    {otherBoards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                  </select>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <button onClick={() => deleteBoard(deletingBoardId)} disabled={boardActionLoading}
                                  className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                                  {boardActionLoading ? 'Deleting…' : 'Delete'}
                                </button>
                                <button onClick={() => setDeletingBoardId(null)} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">Cancel</button>
                              </div>
                            </div>
                          )
                        })()}
                      </ul>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Board name</label>
                          <input
                            autoFocus
                            type="text"
                            value={newBoardName}
                            onChange={e => setNewBoardName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && createBoard()}
                            placeholder="e.g. 2026 Renewals"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                        <button onClick={createBoard} disabled={boardActionLoading || !newBoardName.trim()}
                          className="w-full px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl text-sm font-semibold hover:bg-gray-700 dark:hover:bg-gray-100 disabled:opacity-50 transition-colors">
                          {boardActionLoading ? 'Creating…' : 'Create Board'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )
      })()}

      {/* Demo SMS Banner */}
      {showDemoMessage && (
        <div className="bg-gradient-to-r from-green-100 to-blue-100 dark:from-green-900 dark:to-blue-900 border border-green-300 dark:border-green-700 rounded-xl p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="bg-green-500 rounded-full p-2 flex-shrink-0">
                <span className="text-white text-lg">📱</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-green-800 dark:text-green-200 mb-1">
                  SMS Demo Mode
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                  Twilio SMS integration is in demo mode. Below is the message that would be sent to customers:
                </p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-600">
                  <p className="text-gray-900 dark:text-gray-100 text-sm italic">
                    "{demoMessage}"
                  </p>
                </div>
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  Configure Twilio settings in Settings to enable real SMS notifications.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDemoMessage(false)}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 p-1"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Deals Board - Conditional Rendering based on view mode */}
      {viewSettings && viewSettings.viewMode === 'table' ? (
        // Table View
        <DealsTableView
          statuses={boardData.statuses}
          dealsByStatus={boardData.dealsByStatus}
          onDragEnd={handleDragEnd}
          onDealClick={handleDealClick}
          tableColumns={viewSettings.tableColumns || ['name', 'customer_name', 'amount', 'deal_type', 'net_commission', 'expected_close_date']}
          showEmptyStatuses={showEmptyStatuses}
          onToggleEmptyStatuses={() => setShowEmptyStatuses(!showEmptyStatuses)}
        />
      ) : viewSettings && viewSettings.viewMode === 'compact' ? (
        // Compact View
        <DealsCompactView
          statuses={boardData.statuses}
          dealsByStatus={boardData.dealsByStatus}
          onDragEnd={handleDragEnd}
          onDealClick={handleDealClick}
          showEmptyStatuses={showEmptyStatuses}
          onToggleEmptyStatuses={() => setShowEmptyStatuses(!showEmptyStatuses)}
        />
      ) : (
        // Card View (Original)
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="overflow-x-auto">
            <div className="flex flex-col min-w-max pb-4" style={{ minWidth: 'fit-content' }}>
              {/* Sticky Headers */}
              <div className="sticky top-0 z-10 pb-2">
                <div className="flex gap-6 px-2">
                  {boardData.statuses.map((status) => {
                    const statusTotal = statusTotals[status.id] || 0
                    const totalFormatted = formatAmountInMillions(statusTotal)
                    
                    return (
                      <div key={status.id} className="bg-white dark:bg-gray-800 rounded-xl p-4 w-80 flex-shrink-0 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {status.name}
                          </h3>
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: status.color }}
                          />
                        </div>
                        {totalFormatted && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                            {totalFormatted}
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Content Area */}
              <div className="flex gap-6 px-2">
                {boardData.statuses.map((status) => {
                  return (
                    <div key={status.id} className="w-80 flex-shrink-0">
                      <Droppable droppableId={status.id.toString()}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-3 min-h-[300px] p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm ${
                          snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                      {(boardData.dealsByStatus[String(status.id)] || []).map((deal, index) => (
                        <Draggable key={deal.id} draggableId={deal.id.toString()} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => handleDealClick(deal)}
                              className={`group relative p-4 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-100 dark:border-gray-700 cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg transition-all duration-200 ${
                                snapshot.isDragging ? 'shadow-2xl rotate-2 scale-105 border-blue-400 dark:border-blue-500' : ''
                              }`}
                            >
                              <div className="space-y-2">
                                {/* Customer Name - Full width, no truncation */}
                                <div>
                                  {deal.deal_customers && deal.deal_customers.length > 0 ? (
                                    (() => {
                                      const applicant = deal.deal_customers.find(dc => dc.role === 'applicant')
                                      const coApplicants = deal.deal_customers.filter(dc => dc.role === 'co_applicant')
                                      
                                      return (
                                        <div>
                                          {applicant && (
                                            <h4 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
                                              {applicant.name}
                                            </h4>
                                          )}
                                          {coApplicants.length > 0 && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                              + {coApplicants[0].name}
                                              {coApplicants.length > 1 && ` & ${coApplicants.length - 1} more`}
                                            </p>
                                          )}
                                        </div>
                                      )
                                    })()
                                  ) : (
                                    <h4 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
                                      {deal.customer_name}
                                    </h4>
                                  )}
                                </div>

                                {/* Deal Type & Date Row */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  {deal.type_name && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                      {deal.type_name}
                                    </span>
                                  )}
                                  {deal.expected_close_date && (
                                    <div className="flex items-center text-blue-600 dark:text-blue-400 text-xs font-medium">
                                      <CalendarIcon size={12} className="mr-1" />
                                      <span>{formatDate(deal.expected_close_date)}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Amount & Lender Row */}
                                <div className="flex items-start justify-between pt-1">
                                  {/* Amount & Net Commission */}
                                  <div className="flex flex-col">
                                    <span className="text-2xl font-bold text-green-600 dark:text-green-400 tabular-nums leading-tight">
                                      {formatCompactCurrency(deal.amount)}
                                    </span>
                                    {deal.commission && deal.commission.netCommission > 0 && (() => {
                                      // Calculate broker commission after splits
                                      const splitAmount = (deal.commission_splits || []).reduce((sum, split) => {
                                        const amount = split.split_type === 'percentage'
                                          ? (deal.commission!.netCommission * (split.split_value / 100))
                                          : split.split_value
                                        return sum + amount
                                      }, 0)
                                      const brokerCommission = deal.commission.netCommission - splitAmount
                                      
                                      return (
                                        <span className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                          Net: <span className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(brokerCommission)}</span>
                                        </span>
                                      )
                                    })()}
                                  </div>

                                  {/* Lender Badge */}
                                  {deal.lender_name && (
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                                      <svg className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                        <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                                      </svg>
                                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        {deal.lender_name}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Send to Deal Board — only shown when viewing a lead board */}
                                {boards.find(b => b.id === activeBoardId)?.board_type === 'lead' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      sendToDealBoard(deal.id)
                                    }}
                                    className="mt-2 w-full flex items-center justify-center px-3 py-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-800/30 transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                    Send to Deal Board
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                        {provided.placeholder}
                      </div>
                    )}
                    </Droppable>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </DragDropContext>
      )}

      {/* Add Deal Modal - Enhanced */}
      {showAddDeal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[95vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">{t('deals.createNewDeal')}</h3>
                  <p className="text-blue-100 text-sm">{t('deals.addDealDescription')}</p>
                </div>
                <button
                  onClick={() => { setShowAddDeal(false); setPendingReferralPartner(null) }}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-8 overflow-y-auto max-h-[calc(95vh-120px)]">
            <form onSubmit={handleAddDeal} className="space-y-6">
              {/* Deal Customers */}
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {t('deals.dealCustomers')} *
                </label>
                
                {/* Selected Customers */}
                <div className="space-y-3 mb-4">
                  {selectedCustomers.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                      {t('deals.noCustomersYet')}
                    </p>
                  ) : (
                    selectedCustomers.map((customer, index) => (
                      <div
                        key={customer.customer_id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            customer.role === 'applicant' 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' 
                              : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                          }`}>
                            {customer.role === 'applicant' ? t('deals.applicant') : t('deals.coApplicant')}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {customer.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {customer.email || customer.phone}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {/* Role Toggle */}
                          <select
                            value={customer.role}
                            onChange={(e) => handleChangeCustomerRole(customer.customer_id, e.target.value as 'applicant' | 'co_applicant')}
                            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="applicant">{t('deals.applicant')}</option>
                            <option value="co_applicant">{t('deals.coApplicant')}</option>
                          </select>
                          
                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomer(customer.customer_id)}
                            className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Customer Search */}
                <div className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder={t('deals.searchCustomersPlaceholder')}
                      className="w-full px-4 py-3 pl-12 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <SearchIcon size={20} className="absolute left-4 top-3.5 text-gray-400 dark:text-gray-500" />
                  </div>
                  
                  {/* Customer Dropdown */}
                  {showCustomerDropdown && (
                    <div className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
                      {customers.length > 0 ? (
                        customers.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => handleAddCustomer(customer)}
                            className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-gray-600 focus:bg-blue-50 dark:focus:bg-gray-600 focus:outline-none border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors"
                          >
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {customer.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {customer.email || customer.phone}
                            </div>
                          </button>
                        ))
                      ) : customerSearch.trim().length > 0 ? (
                        <div className="p-4">
                          <p className="text-gray-600 dark:text-gray-400 text-center mb-3">
                            {t('deals.noCustomersFound')}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateCustomerModal(true)
                              setShowCustomerDropdown(false)
                            }}
                            className="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                          >
                            + {t('deals.createNewCustomer')}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Referral partner badge — shown when a partner was picked during customer creation */}
              {pendingReferralPartner && (
                <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">{pendingReferralPartner.name}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      {t('deals.referralPartnerWillBeAdded', { defaultValue: 'Referral partner — will be added to commission splits' })}
                      {pendingReferralPartner.commission_cut != null && ` (${pendingReferralPartner.commission_cut}%)`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingReferralPartner(null)}
                    className="text-blue-400 hover:text-red-500 transition-colors"
                    title={t('common.remove', { defaultValue: 'Remove' })}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Deal Type & Amount Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Deal Type */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-gray-800 rounded-full mr-2"></span>
                    {t('deals.dealType')}
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {dealTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setNewDeal({ ...newDeal, deal_type_id: type.id.toString() })}
                        className={`flex-1 min-w-[120px] px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                          newDeal.deal_type_id === type.id.toString()
                            ? 'bg-gradient-to-r from-gray-800 to-indigo-600 text-white shadow-lg scale-105'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border-2 border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        {type.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Amount */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    {t('deals.loanAmount')} *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3.5 text-gray-500 dark:text-gray-400 text-lg font-semibold">$</span>
                    <input
                      type="text"
                      value={newDeal.amount}
                      onChange={(e) => setNewDeal({ ...newDeal, amount: e.target.value })}
                      className="w-full px-4 py-3 pl-8 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="250,000 or 250k or 1.5M"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Supports formats like 250k, 1.5M, or 250,000
                  </p>
                </div>
              </div>

              {/* Closing Date & Status Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Closing Date */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    {t('deals.expectedCloseDate')}
                  </label>
                  <input
                    type="date"
                    value={newDeal.closing_date}
                    onChange={(e) => setNewDeal({ ...newDeal, closing_date: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-lg"
                  />
                </div>

                {/* Initial Status */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    {t('deals.initialStatus')} *
                  </label>
                  <select
                    value={newDeal.status_id}
                    onChange={(e) => setNewDeal({ ...newDeal, status_id: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-lg"
                    required
                  >
                    <option value="">{t('deals.selectStatus')}</option>
                    {boardData.statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                  {t('deals.descriptionNotes')}
                </label>
                <textarea
                  rows={4}
                  value={newDeal.description}
                  onChange={(e) => setNewDeal({ ...newDeal, description: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 text-lg placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                  placeholder={t('deals.descriptionPlaceholder')}
                />
              </div>

              {/* Form Buttons */}
              <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => { setShowAddDeal(false); setPendingReferralPartner(null) }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-semibold text-lg"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  {t('deals.createDeal')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}



      {/* Archived Deals Modal */}
      {showArchivedDeals && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('deals.archivedDeals')}</h2>
              <button
                onClick={() => setShowArchivedDeals(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loadingArchived ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : archivedDeals.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">{t('deals.noArchivedDeals')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {archivedDeals.map((deal) => (
                    <div
                      key={deal.id}
                      onClick={() => {
                        navigate(`/dashboard/deals/${deal.id}`)
                        setShowArchivedDeals(false)
                      }}
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                          {deal.name || deal.title || 'Untitled Deal'}
                        </h4>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <p><strong>Customer:</strong> {deal.customer_name}</p>
                          <p><strong>Amount:</strong> {formatCurrency(deal.amount)}</p>
                          {deal.commission && deal.commission.netCommission > 0 && (() => {
                            // Calculate broker commission after splits
                            const splitAmount = (deal.commission_splits || []).reduce((sum, split) => {
                              const amount = split.split_type === 'percentage'
                                ? (deal.commission!.netCommission * (split.split_value / 100))
                                : split.split_value
                              return sum + amount
                            }, 0)
                            const brokerCommission = deal.commission.netCommission - splitAmount
                            
                            return (
                              <p><strong>Net Commission:</strong> <span className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(brokerCommission)}</span></p>
                            )
                          })()}
                          <p><strong>Status:</strong> {deal.status_name}</p>
                          {deal.closing_date && (
                            <p><strong>Closing:</strong> {formatDate(deal.closing_date)}</p>
                          )}
                          <p><strong>Archived:</strong> {formatDate(deal.updated_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Funding Confirmation Modal (placeholder to keep structure intact) */}
      {false && (
        <div>
        </div>
      )}

      {/* Funding Confirmation Modal */}
      {showFundingModal && pendingMove && (
        <FundingConfirmationModal
          deal={{
            ...pendingMove.deal,
            title: pendingMove.deal.name,
            amount: typeof pendingMove.deal.amount === 'string' ? parseFloat(pendingMove.deal.amount) : pendingMove.deal.amount,
            base_points: typeof pendingMove.deal.base_points === 'string' ? parseInt(pendingMove.deal.base_points) : parseInt(pendingMove.deal.base_points || '0'),
            bonus_points: typeof pendingMove.deal.bonus_points === 'string' ? parseInt(pendingMove.deal.bonus_points) : parseInt(pendingMove.deal.bonus_points || '0'),
            volume_points: typeof pendingMove.deal.volume_points === 'string' ? parseInt(pendingMove.deal.volume_points) : parseInt(pendingMove.deal.volume_points || '0')
          }}
          isOpen={showFundingModal}
          onConfirm={handleFundingConfirm}
          onCancel={handleFundingCancel}
        />
      )}

      {/* Financial Validation Modal */}
      {showValidationModal && validationError && (
        <FinancialValidationModal
          isOpen={showValidationModal}
          onClose={handleValidationClose}
          errorType={validationError.type}
          currentFlag={validationError.currentFlag}
          attemptedFlag={validationError.attemptedFlag}
        />
      )}

      {/* Deal Status Rules — Missing Required Fields Modal */}
      {showDealValidationModal && dealValidationState && (
        <DealValidationModal
          isOpen={showDealValidationModal}
          dealId={dealValidationState.dealId}
          dealData={dealValidationState.dealData}
          missingFields={dealValidationState.missingFields}
          targetStatusId={dealValidationState.targetStatusId}
          targetStatusName={dealValidationState.targetStatusName}
          onSuccess={() => {
            setShowDealValidationModal(false)
            setDealValidationState(null)
            fetchDealsData()
          }}
          onClose={() => {
            setShowDealValidationModal(false)
            setDealValidationState(null)
          }}
        />
      )}

      {/* Associated Customers Prompt Modal */}
      {showAssociatedCustomersPrompt && pendingCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('deals.associatedCustomersPrompt.title', { defaultValue: 'Add Associated Customers?' })}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('deals.associatedCustomersPrompt.description', { 
                      defaultValue: `${pendingCustomer.name} has ${associatedCustomers.length} associated ${associatedCustomers.length === 1 ? 'customer' : 'customers'}. Would you like to add them to this deal as co-applicants?`,
                      customerName: pendingCustomer.name,
                      count: associatedCustomers.length
                    })}
                  </p>
                  <div className="space-y-2 mb-4">
                    {associatedCustomers.map((customer) => (
                      <div key={customer.id} className="flex items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                          <span className="text-gray-900 dark:text-gray-600 font-bold text-sm">
                            {customer.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.name}</p>
                          {customer.email && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{customer.email}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSkipAssociatedCustomers}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  {t('deals.associatedCustomersPrompt.skip', { defaultValue: 'Skip' })}
                </button>
                <button
                  onClick={handleAddAllAssociatedCustomers}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  {t('deals.associatedCustomersPrompt.addAll', { defaultValue: 'Add All as Co-Applicants' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Customer Modal */}
      {showCreateCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('deals.createNewCustomer')}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateCustomerModal(false)
                    setNewCustomerData({ first_name: '', last_name: '', email: '', phone: '', referred_by: '' })
                    setSelectedReferralPartner(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.firstName')} *
                  </label>
                  <input
                    type="text"
                    value={newCustomerData.first_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    placeholder={t('customers.form.enterFirstName')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.lastName')} *
                  </label>
                  <input
                    type="text"
                    value={newCustomerData.last_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    placeholder={t('customers.form.enterLastName')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.emailAddress')} *
                  </label>
                  <input
                    type="email"
                    value={newCustomerData.email}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    placeholder={t('customers.form.enterEmail')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.phoneNumber')}
                  </label>
                  <input
                    type="tel"
                    value={newCustomerData.phone}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    placeholder={t('customers.form.enterPhone')}
                  />
                </div>

                {/* Referred By — searchable partner picker */}
                <PartnerSearchField
                  selected={selectedReferralPartner}
                  onSelect={setSelectedReferralPartner}
                  onClear={() => setSelectedReferralPartner(null)}
                  label={t('customers.form.referredBy')}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateCustomerModal(false)
                    setNewCustomerData({ first_name: '', last_name: '', email: '', phone: '', referred_by: '' })
                    setSelectedReferralPartner(null)
                  }}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                  disabled={creatingCustomer}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleCreateCustomer}
                  disabled={creatingCustomer}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingCustomer ? t('customers.form.saving', { defaultValue: 'Saving...' }) : t('customers.form.createCustomer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Board Status Editor slide-over */}
      <BoardStatusPanel
        boardId={activeBoardId}
        boardName={boards.find(b => b.id === activeBoardId)?.name ?? 'Board'}
        isOpen={showStatusPanel}
        onClose={() => setShowStatusPanel(false)}
        onStatusesChanged={fetchDealsData}
      />
    </FullWidthContent>
  )
}

export default DealsBoard